import { fromZonedTime, getTimezoneOffset } from 'date-fns-tz';

import { OrderStateV2, JobDraftV2, ShiftDraftV2 } from '../context';

import { CreateOrderPayloadV2, LocationListItem, SurgeRateV2 } from './types';

// Shifts are held in state as naive wall-time strings ("YYYY-MM-DDTHH:MM:SS")
// representing the time at the job's location. The backend stores instants in
// UTC, so we resolve the wall time to UTC at the submission boundary using the
// location's IANA timezone. Without this, the backend interprets the naive
// string as UTC and the displayed shift drifts by the offset (e.g. 6 AM EDT
// rendered as 2 AM EDT).
const MS_PER_HOUR = 60 * 60 * 1000;

const resolveTimezone = (tz: string | null | undefined): string =>
  tz || Intl.DateTimeFormat().resolvedOptions().timeZone;

const toUtcIso = (wallTime: string, timezone: string): string =>
  fromZonedTime(wallTime, timezone).toISOString();

const offsetHoursFor = (wallTime: string, timezone: string): number => {
  const instant = fromZonedTime(wallTime, timezone);
  return getTimezoneOffset(timezone, instant) / MS_PER_HOUR;
};

const surgeRateForDate = (
  surgeRates: SurgeRateV2[],
  shift: ShiftDraftV2,
): SurgeRateV2 | null => {
  // Shifts hold naive wall-time strings ("YYYY-MM-DDTHH:MM:SS"); the date
  // portion already matches the local-calendar date the user selected.
  const dateKey = shift.start_at.slice(0, 10);
  return surgeRates.find((sr) => sr.date === dateKey) || null;
};

type WireShift = {
  start_at: string;
  end_at: string;
  time_zone_offset: number;
  break_minutes?: number;
  quantity?: number;
};

// Emits one job payload per shift, mirroring legacy `jobStateToJobInput` in
// `CreateGigOrder/context.ts` where each Schedule (always a single calendar
// day in the live UI) produced its own `JobInput`. The legacy `groupId`
// rule is preserved: shifts share `group_id` when `all_shifts_same_worker`
// is true, otherwise each shift gets a sequential `group_id`. Surge-rate
// detection runs per-shift so each emitted job carries the correct
// `is_holiday`/multiplied rates/original rates.
const expandJobToPerShiftPayloads = (
  job: JobDraftV2,
  shifts: ShiftDraftV2[],
  tz: string,
  surgeRates: SurgeRateV2[],
  startGroupId: number,
): { payloads: Record<string, unknown>[]; nextGroupId: number } => {
  const sameWorker = !!job.all_shifts_same_worker;
  const visibility =
    job.staffing_strategy === 'OPEN_JOB_POST' ? 'PUBLIC' : 'PRIVATE';
  const publish = visibility === 'PRIVATE' ? job.auto_fill === true : false;
  // V1 parity: hires/invites apply only to schedules the admin had
  // selected on the Staffing screen. The UI mirrors deselections onto
  // `unselected_shift_indexes`; deselected indexes drop the worker
  // assignments on the corresponding emitted job (the shift still gets
  // created, but unstaffed). Undefined / empty means "all shifts
  // selected", which preserves single-shift legacy behavior.
  const unselectedSet = new Set(job.unselected_shift_indexes ?? []);

  const payloads: Record<string, unknown>[] = shifts.map((shift, idx) => {
    const surge = surgeRateForDate(surgeRates, shift);
    const groupId = sameWorker ? startGroupId : startGroupId + idx;
    const wireShift: WireShift = {
      ...shift,
      start_at: toUtcIso(shift.start_at, tz),
      end_at: toUtcIso(shift.end_at, tz),
      time_zone_offset: offsetHoursFor(shift.start_at, tz),
    };

    const {
      staffing_strategy,
      auto_fill,
      shifts: _omitShifts,
      unselected_shift_indexes: _omitUnselected,
      invite_workers,
      hire_workers,
      ...rest
    } = job;
    void staffing_strategy;
    void auto_fill;
    void _omitShifts;
    void _omitUnselected;

    // Deselected shifts emit jobs WITHOUT the invite/hire arrays — the
    // shift is still created but no workers are pre-attached to it,
    // matching legacy "hire only on selected schedules".
    const isShiftUnselected = unselectedSet.has(idx);
    const inviteWorkersForShift = isShiftUnselected
      ? []
      : (invite_workers ?? []);
    const hireWorkersForShift = isShiftUnselected ? [] : (hire_workers ?? []);

    // V1 parity: each schedule produced a JobInput with that schedule's
    // own worker count. V2 stored a single `job.quantity` and spread it
    // through `...rest`, so a draft with shifts {qty 1, qty 3} emitted
    // two jobs both at qty 3. Override per emitted job with the shift's
    // own quantity, falling back to job.quantity (then 1) so legacy
    // single-quantity orders keep behaving exactly as before.
    const perShiftQuantity = shift.quantity ?? job.quantity ?? 1;
    // Same fix for break duration: the backend Job model has a
    // job-level `mandatory_break_time` column (see
    // gravywork-graphy/app/models/job.rb#mandatory_break_time and
    // job_creation_service.rb), and v2 was emitting the job-level
    // `mandatory_break_time` clobbered by `writeShifts` to the last
    // edited break. Override per emitted job with that shift's own
    // `break_minutes` so each shift persists its own break.
    const perShiftBreak = shift.break_minutes ?? job.mandatory_break_time ?? 0;

    if (surge) {
      return {
        ...rest,
        shifts: [wireShift],
        job_type: rest.job_type,
        visibility,
        publish,
        group_id: groupId,
        quantity: perShiftQuantity,
        mandatory_break_time: perShiftBreak,
        invite_workers: inviteWorkersForShift,
        hire_workers: hireWorkersForShift,
        is_holiday: true,
        pay_rate: Math.round(job.pay_rate * surge.rate),
        cost_rate: Math.round(job.cost_rate * surge.rate),
        original_pay_rate: job.pay_rate,
        original_cost_rate: job.cost_rate,
      };
    }
    // Always derive `original_*` from the current pay/cost on the job
    // rather than `...rest` (which carries `state.original_*`). On a
    // markup billing account, the backend validates
    // `valid_markup_rates?(original_pay_rate, original_cost_rate,
    // markup_percent)` — if those are stale (e.g. a template was loaded
    // onto an account with a different markup, or a rate quote happened
    // before the user edited the pay rate), the invariant
    // `cost = pay + pay * markup / 100` no longer holds against the
    // current markup and the submission fails with "Invalid markup rates".
    // v1's `updSchedulesWithHolidayRates` keeps original_* aligned with
    // calcPayRate/calcBillRate the same way (CreateGigOrder Schedule util).
    return {
      ...rest,
      shifts: [wireShift],
      job_type: rest.job_type,
      visibility,
      publish,
      group_id: groupId,
      quantity: perShiftQuantity,
      mandatory_break_time: perShiftBreak,
      invite_workers: inviteWorkersForShift,
      hire_workers: hireWorkersForShift,
      original_pay_rate: job.pay_rate,
      original_cost_rate: job.cost_rate,
    };
  });

  const nextGroupId = sameWorker
    ? startGroupId + 1
    : startGroupId + shifts.length;

  return { payloads, nextGroupId };
};

export const buildCreatePayload = (
  state: OrderStateV2,
  locations: LocationListItem[] = [],
  surgeRates: SurgeRateV2[] = [],
): CreateOrderPayloadV2 => {
  const jobs: Record<string, unknown>[] = [];
  let groupCounter = 1;

  state.jobs.forEach((job) => {
    const location = locations.find((loc) => loc.id === job.address_id);
    const tz = resolveTimezone(location?.timezone);
    const shifts = job.shifts || [];
    // v1 parity: legacy emitted no JobInput for a Schedule-less job draft.
    // Mirror that here — a draft with no shifts contributes no payload.
    if (shifts.length === 0) {
      return;
    }
    const normalizedJob: JobDraftV2 = {
      ...job,
      job_type: state.orderType || job.job_type,
    };
    const { payloads, nextGroupId } = expandJobToPerShiftPayloads(
      normalizedJob,
      shifts,
      tz,
      surgeRates,
      groupCounter,
    );
    payloads.forEach((p) => jobs.push(p));
    groupCounter = nextGroupId;
  });

  return {
    account_id: state.billingAccount?.id || 0,
    order_type: (state.orderType || 'GIG') as 'GIG' | 'LTA',
    verification_enabled: !!state.verificationEnabled,
    jobs: jobs as CreateOrderPayloadV2['jobs'],
  };
};
